import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * One segment of the Incepted APE breakdown.
 *
 * Today this is populated as [Singlife, Other Companies] from the aggregated
 * API. When the backend starts returning a per-company array, each company
 * becomes its own segment — the card renders N segments generically.
 */
export interface ApeSegmentV2 {
  name: string;
  ape: number;
  cases: number;
  logoUrl?: string;
}

// ── Shared colour system (card legend + drawer tiles stay in sync) ─
// Validated categorical palette (see dataviz palette validator): the top
// carriers take these hues in rank order; everyone else is neutral "Other".
export const APE_PALETTE = ['#2f6fb0', '#16a085', '#c47f10', '#d1495b', '#9d4edf'];
export const APE_NEUTRAL = '#c9d2de';
/** How many carriers get their own colour/slice before folding into "Other". */
export const APE_TOP_N = 5;

/** Colour for a segment at a given rank — shared by bar, legend, donut and tiles. */
export function apeColorAt(rankIndex: number): string {
  return rankIndex < APE_TOP_N ? APE_PALETTE[rankIndex] : APE_NEUTRAL;
}

/** Segments ordered by APE, largest first (leaderboard order). */
export function rankApeSegments(segments: ApeSegmentV2[]): ApeSegmentV2[] {
  return [...segments].sort((a, b) => b.ape - a.ape);
}

/**
 * Compact "Incepted APE" summary card: KPIs + donut + legend, plus a
 * "View by carrier" toggle. The detailed per-carrier drawer is rendered
 * separately (full-width) by the parent — this card only emits the toggle.
 */
@Component({
  selector: 'app-incepted-ape-card-v2',
  imports: [CommonModule],
  templateUrl: './incepted-ape-card-v2.component.html',
  styleUrl: './incepted-ape-card-v2.component.scss'
})
export class InceptedApeCardComponentV2 {
  @Input() title = 'Incepted APE';
  @Input() period = '';
  @Input() segments: ApeSegmentV2[] = [];
  /** Whether this card's drawer is currently open (parent-controlled). */
  @Input() expanded = false;
  /** Emitted when the user clicks "View by carrier". */
  @Output() toggleView = new EventEmitter<void>();

  onToggle(): void {
    this.toggleView.emit();
  }

  /** Segment's share of the total, as a percentage (0 when total is 0). */
  pct(segment: ApeSegmentV2): number {
    return this.total > 0 ? (segment.ape / this.total) * 100 : 0;
  }

  /** Combined case count across all segments. */
  get totalCases(): number {
    return this.segments.reduce((sum, seg) => sum + (seg.cases || 0), 0);
  }

  get total(): number {
    return this.segments.reduce((sum, seg) => sum + (seg.ape || 0), 0);
  }

  /** Number of carriers/segments in the breakdown. */
  get count(): number {
    return this.segments.length;
  }

  /** Segments ordered by APE, largest first (leaderboard order). */
  get rankedSegments(): ApeSegmentV2[] {
    return rankApeSegments(this.segments);
  }

  /** The largest carrier — highlighted in the donut centre. */
  get leader(): ApeSegmentV2 | undefined {
    return this.rankedSegments[0];
  }

  private readonly donutRadius = 50;
  readonly donutC = 2 * Math.PI * this.donutRadius;

  colorAt(rankIndex: number): string {
    return apeColorAt(rankIndex);
  }

  /**
   * Donut slices: the top-N carriers plus a single aggregated "Other".
   * Each slice carries its stroke dash geometry (with a 2px surface gap).
   */
  get donutSlices(): Array<{ color: string; dash: number; offset: number; name: string; ape: number; pct: number }> {
    const ranked = this.rankedSegments;
    const gap = 2;

    const parts = ranked.slice(0, APE_TOP_N).map((seg, i) => ({
      color: APE_PALETTE[i],
      name: seg.name,
      ape: seg.ape,
      pct: this.pct(seg)
    }));

    const rest = ranked.slice(APE_TOP_N);
    if (rest.length) {
      const restApe = rest.reduce((sum, seg) => sum + seg.ape, 0);
      parts.push({
        color: APE_NEUTRAL,
        name: `Other (${rest.length})`,
        ape: restApe,
        pct: this.total > 0 ? (restApe / this.total) * 100 : 0
      });
    }

    let cumulative = 0;
    return parts.map(part => {
      const length = (part.pct / 100) * this.donutC;
      const slice = {
        ...part,
        dash: Math.max(length - gap, 0),
        offset: -cumulative
      };
      cumulative += length;
      return slice;
    });
  }
}
